import React from 'react';
import { Row, Col, InputNumber, Button, Badge, Radio, Select, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import AdViewComponent from './AdViewComponent';
import DisplayViewComponent from './DisplayViewComponent';
import ImpressionManagementHolder from '../../../styles/ImpressionManagementStyle';
import useBudgetTransfer from '../../../hooks/investment/useBudgetTransfer';
import Colors from '../../../../../../themes/Colors';

const ImpressionManagementComponent = props => {
  const { t } = useTranslation();
  const {
    currentCampaign,
    reportSingleData,
    customContents,
    transferModalVisible,
    cart,
    showTransferModal,
    updateImpressionsDisplay,
    editCampaignActive,
    initialBackup,
    hasInitialBackup
  } = props;

  const {
    contentsDisplaysSelect,
    view,
    displayOptions,
    displayFilter,
    creditAvailable,
    // subsidizedCreditAvailable,
    adVersions,
    originVersion,
    destinationVersion,
    originScreen,
    destinationScreen,
    transferPercentage,
    format,
    setTransferPercentage,
    setOriginVersion,
    setDestinationVersion,
    setOriginScreen,
    setDestinationScreen,
    handleRadioChange,
    handleTransfer,
    handleChangeDisplayFilter,
    handleSearchDisplayFilter,
    updateImpressions,
    updateImpressionsToMax,
    updateBudget,
    updateImpressionsCustomContents,
    calculateBudget,
    getMaxBudgetForRecord,
    calculateDiffValue,
    calculateImpressionsView,
    getCurrentSubsidizedShows,
    // calculateConsumedImpressions,
    getMinSpotsForRecord,
    getRecordFloorReasons,
    checkCreditAvailable,
    limitAddImpressions
  } = useBudgetTransfer({
    currentCampaign,
    reportSingleData,
    customContents,
    transferModalVisible,
    cart,
    updateImpressionsDisplay,
    showTransferModal,
    editCampaignActive
  });

  // JSX render helpers — kept in component because they return React elements
  const messageStatus = item => {
    if (item.subsidized_price) {
      if (item.promised_shows !== 0) {
        return (
          <span className="ant-tag ant-tag-orange" style={{ marginLeft: '5px', fontSize: '10px' }}>
            {t('Subsidized')}
          </span>
        );
      }
      return (
        <span className="ant-tag ant-tag-blue" style={{ marginLeft: '5px', fontSize: '10px' }}>
          {t('Suspended')}
        </span>
      );
    }
    if (
      (item.promised_shows_origin !== item.promised_shows_update
        ? item.promised_shows_update
        : calculateImpressionsView(item)) === 0
    ) {
      return (
        <span style={{ color: 'red', marginLeft: '5px', fontSize: '12px' }}>
          {t('It will be in suspended state if the value is 0.')}
        </span>
      );
    }
  };

  const getSpotStatusTag = (item, t) => {
    const contentDisplay = currentCampaign.contents_displays.find(
      cd =>
        cd.id === item.relationId &&
        cd.display_id === item.displayId &&
        cd.content_id === item.content.id
    );
    if (!contentDisplay) return null;
    const { revision_status: revisionStatus } = contentDisplay;
    const isActive = item.active;
    if (revisionStatus === 'approved' && isActive === 1) {
      return (
        <Tooltip title={t('Spot Approved')}>
          <Badge color="green" />
        </Tooltip>
      );
    }
    if (isActive === 0) {
      return (
        <Tooltip title={t('Paused')}>
          <Badge color="yellow" />
        </Tooltip>
      );
    }
    if (revisionStatus === 'rejected') {
      return (
        <Tooltip title={t('Spot Rejected')}>
          <Badge color="red" />
        </Tooltip>
      );
    }
    if (revisionStatus === 'pending') {
      return (
        <Tooltip title={t('The owner of this screen has not yet approved')}>
          <Badge color="orange" />
        </Tooltip>
      );
    }
    if (revisionStatus === 'out_of_service') {
      return (
        <Tooltip title={t('Display out of service')}>
          <Badge color="red" />
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <ImpressionManagementHolder>
      <Row>
        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="row-info">
            <p style={{ marginRight: '10px', paddingTop: '5px' }}>{t('Available credit')}</p>
            <InputNumber
              disabled
              min={0}
              value={Math.max(0, creditAvailable)}
              formatter={value => (value === 0 ? format.format(0) : format.format(value))}
              style={{ width: '150px' }}
              className="text-impression-disabled-important"
            />
            {/* <InputNumber
              disabled
              min={0}
              value={Math.max(0, subsidizedCreditAvailable)}
              formatter={value => (value === 0 ? format.format(0) : format.format(value))}
              style={{ width: '150px', color: 'orange' }}
              className="text-impression-disabled-important"
            /> */}
          </div>
        </Col>
        <Col xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="row-info" style={{ justifyContent: 'flex-end' }}>
            <Radio.Group
              onChange={handleRadioChange}
              value={view}
              buttonStyle="solid"
              defaultValue="ad"
            >
              <Radio.Button value="ad">{t('By Ad')}</Radio.Button>
              <Radio.Button value="display">{t('By Display')}</Radio.Button>
            </Radio.Group>
          </div>
        </Col>
      </Row>
      <Row gutter={8}>
        <Col xs={10} sm={10} md={10} lg={10} xl={10}>
          <div className="row-info">
            <p style={{ marginRight: '10px', paddingTop: '5px' }}>{t('Filter displays')}</p>
            <Select
              mode="multiple"
              style={{ flex: 1 }}
              placeholder={t('Filter displays')}
              defaultValue={['all']}
              value={displayFilter}
              filterOption={false}
              onSearch={handleSearchDisplayFilter}
              onChange={handleChangeDisplayFilter}
            >
              <Select.Option key="all" value="all">
                {t('All displays')}
              </Select.Option>
              {displayOptions.map((item, index) => (
                <Select.Option key={index} value={item.id}>
                  <span
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '300px',
                      display: 'block'
                    }}
                  >
                    {item.name}
                  </span>
                </Select.Option>
              ))}
            </Select>
          </div>
        </Col>
        <Col xs={14} sm={14} md={14} lg={14} xl={14}>
          <div
            className="row-info"
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            {view === 'ad' ? (
              <div>
                <span style={{ marginRight: '5px' }}>{t('Transfer the')}</span>
                <InputNumber
                  min={0}
                  max={100}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                  style={{ width: '80px', marginRight: '5px' }}
                  value={transferPercentage}
                  onChange={setTransferPercentage}
                />
                <span style={{ marginRight: '5px' }}>{t('of impressions from')}</span>
                <Select
                  style={{ width: '120px', marginRight: '5px' }}
                  placeholder={t('Origin Version')}
                  onChange={setOriginVersion}
                  value={originVersion}
                  id="origin-version"
                >
                  {adVersions.map(version => (
                    <Select.Option key={version} value={version}>
                      {version}
                    </Select.Option>
                  ))}
                </Select>
                <span style={{ marginRight: '5px' }}>{t('to')}</span>
                <Select
                  style={{ width: '120px' }}
                  placeholder={t('Destination Version')}
                  onChange={setDestinationVersion}
                  value={destinationVersion}
                  id="destination-version"
                >
                  {adVersions.map((item, index) => (
                    <Select.Option key={index} value={item}>
                      {item}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            ) : view === 'display' ? (
              <div>
                <span style={{ marginRight: '5px' }}>{t('Transfer the')}</span>
                <InputNumber
                  min={0}
                  max={100}
                  formatter={value => `${value}%`}
                  placeholder="%"
                  parser={value => value.replace('%', '')}
                  style={{ width: '80px', marginRight: '5px' }}
                  value={transferPercentage}
                  onChange={setTransferPercentage}
                />
                <span style={{ marginRight: '5px' }}>{t('of impressions from')}</span>
                <Select
                  style={{ width: '120px', marginRight: '5px' }}
                  placeholder={t('Origin Screen')}
                  onChange={setOriginScreen}
                  value={originScreen}
                  id="origin-screen"
                >
                  {cart
                    .filter(
                      screen => displayFilter.includes('all') || displayFilter.includes(screen.id)
                    )
                    .map(display => (
                      <Select.Option key={display.id} value={display.id} title={display.name}>
                        {display.name}
                      </Select.Option>
                    ))}
                </Select>
                <span style={{ marginRight: '5px' }}>{t('to')}</span>
                <Select
                  style={{ width: '120px' }}
                  placeholder={t('Destination Screen')}
                  onChange={setDestinationScreen}
                  value={destinationScreen}
                  id="destination-screen"
                >
                  {cart
                    .filter(
                      display => displayFilter.includes('all') || displayFilter.includes(display.id)
                    )
                    .map(display => (
                      <Select.Option key={display.id} value={display.id} title={display.name}>
                        {display.name}
                      </Select.Option>
                    ))}
                </Select>
              </div>
            ) : null}
            <Button
              type="primary"
              onClick={handleTransfer}
              disabled={
                view === 'ad'
                  ? !originVersion || !destinationVersion || originVersion === destinationVersion
                  : view === 'display'
                    ? !originScreen || !destinationScreen || originScreen === destinationScreen
                    : true || transferPercentage === 0
              }
            >
              {t('Apply')}
            </Button>
          </div>
        </Col>
      </Row>
      {view === 'ad' ? (
        <div className="table-container">
          <AdViewComponent
            contentsDisplaysSelect={contentsDisplaysSelect}
            currency={currentCampaign.currency}
            updateImpressions={updateImpressions}
            updateImpressionsToMax={updateImpressionsToMax}
            getSpotStatusTag={getSpotStatusTag}
            messageStatus={messageStatus}
            calculateDiffValue={calculateDiffValue}
            calculateImpressionsView={calculateImpressionsView}
            getCurrentSubsidizedShows={getCurrentSubsidizedShows}
            // calculateConsumedImpressions={calculateConsumedImpressions}
            limitAddImpressions={limitAddImpressions}
            calculateBudget={calculateBudget}
            getMaxBudgetForRecord={getMaxBudgetForRecord}
            updateBudget={updateBudget}
            getMinSpotsForRecord={getMinSpotsForRecord}
            getRecordFloorReasons={getRecordFloorReasons}
            editCampaignActive={editCampaignActive}
            initialBackup={initialBackup}
            hasInitialBackup={hasInitialBackup}
          />
        </div>
      ) : view === 'display' ? (
        <div className="table-container">
          <DisplayViewComponent
            contentsDisplaysSelect={contentsDisplaysSelect}
            currency={currentCampaign.currency}
            calculateDiffValue={calculateDiffValue}
            calculateImpressionsView={calculateImpressionsView}
            getRecordFloorReasons={getRecordFloorReasons}
            // calculateConsumedImpressions={calculateConsumedImpressions}
            editCampaignActive={editCampaignActive}
            initialBackup={initialBackup}
            hasInitialBackup={hasInitialBackup}
          />
        </div>
      ) : null}
      <Row>
        <Col
          className="action-button"
          style={{ marginBottom: '0px' }}
          type="flex"
          align="end"
          xs={24}
          sm={24}
          md={24}
          lg={24}
          xl={24}
        >
          {creditAvailable > 0 && (
            <p style={{ marginBottom: '0px', fontSize: '10px', color: Colors.atention }}>
              *
              {t(
                checkCreditAvailable()
                  ? 'You have available credit. You must use it to continue distributing.'
                  : 'Consumed credit. You can apply changes and edit. The remaining credit (n_credite) cannot be used to purchase more impressions.'
              ).replace('n_credite', format.format(creditAvailable))}
            </p>
          )}
        </Col>
        <Col
          className="action-button"
          type="flex"
          align="end"
          xs={24}
          sm={24}
          md={24}
          lg={24}
          xl={24}
        >
          <Button key="Cancel" onClick={showTransferModal}>
            {t('Cancel')}
          </Button>
          <Button
            key="Apply"
            type="primary"
            onClick={updateImpressionsCustomContents}
            disabled={checkCreditAvailable()}
          >
            {t('Apply changes')}
          </Button>
        </Col>
      </Row>
    </ImpressionManagementHolder>
  );
};

export default ImpressionManagementComponent;
